import {
  MutationCache,
  QueryClient,
  onlineManager,
  type DefaultOptions,
  type Mutation,
} from '@tanstack/react-query';
import { createStore, del, set, update, values, type UseStore } from 'idb-keyval';
import { REPLAYABLE, pathOf } from './offline-replay';
import { refusalStatus } from './refusal';
import { onSignalOrVisible } from './browser';
import { idOf, keyOf } from './idempotency';
import { isSessionQueryKey } from './cache-account';
import {
  accountRef,
  parseAccountRef,
  sameAccount,
  sameAccountOrNobody,
  type AccountRef,
} from './account';
import {
  DuplicateWriteError,
  NeedsConnectionError,
  NoSignalError,
  ParkedWriteError,
  StorageFullError,
  UnkeyedWriteError,
  UnownedWriteError,
  isKeptForAccount,
  shouldRetryWrite,
} from './write-refusals';

/**
 * A queued write belongs to the account that made it (MAP 225b).
 *
 * **The store is the truth; TanStack's memory is a cache of the current account's part of
 * it.**
 *
 * - Every replayable write gets a durable row, keyed by its idempotency key and stamped with
 *   the account that made it (a person in a shop), BEFORE its first network attempt
 *   (`onMutate`, which TanStack awaits before `retryer.start()`).
 * - A row leaves the store only when the server took the write (`onSuccess`), or when its
 *   refusal has been written to the failed-write log under the same account (`onError`).
 *   Everything else (pauses, retries, account changes, stopped tabs, sign-out, 401s) leaves
 *   the row where it is. In the worst case a write waits; it is never lost and never sent as
 *   someone else.
 *
 * Pauses and retries never touch the disk: TanStack calls the cache's `onError` once, on the
 * final failure. At boot every row is replayed the same way, with the same key, whatever its
 * last phase was, so a saved phase would cost a write per attempt and change no decision.
 */

/** One replayable write waiting on this device. */
export type PendingWrite = Readonly<{
  /** `clientId:mutationId`: the server dedupes on that pair, so it is the write's identity. */
  id: string;
  clientId: string;
  mutationId: string;
  /** tRPC procedure path: `payment.recordManual`. */
  path: string;
  /** The write's input, verbatim (idempotency key and all), so a replay is the same request. */
  variables: Record<string, unknown>;
  /** The account that made it. Never whoever is signed in later. */
  madeBy: AccountRef;
  createdAt: number;
  /** `live`: made in this release; `migrated`: moved out of an older saved cache. */
  source: 'live' | 'migrated';
  /**
   * The draft this write's text was typed in, as its screen names it (`meta.draftKey` on the
   * mutation): when the write lands, that draft, and only that one, is cleared. None for a
   * write with no draft, or one made before 225b.
   */
  draftKey?: string;
}>;

/** The draft a mutation's screen named in its `meta`, if any. */
function draftKeyOf(meta: unknown): string | undefined {
  const key = (meta as { draftKey?: unknown } | undefined)?.draftKey;
  return typeof key === 'string' ? key : undefined;
}

/** Durable side. An interface so the logic is testable without IndexedDB. */
export interface PendingWriteStore {
  all(): Promise<unknown[]>;
  put(row: PendingWrite): Promise<void>;
  remove(id: string): Promise<void>;
}

/** Where a final refusal goes: the failed-write log. */
export interface WriteHandOff {
  /** Must reject unless the row reached the disk: the pending row is removed only after. */
  record(input: {
    path: string;
    variables: unknown;
    error: unknown;
    account: AccountRef;
  }): Promise<void>;
  /** The write with this idempotency key landed, by whatever route. */
  landed(mutationId: string): Promise<void>;
}

/**
 * The closed list of mutations that may be made with NO account: they run before an account
 * exists or across one, by design. Joining a shop (`/join`, signed in, no shop yet) and a
 * customer's answer to a quote (`/q/:token`, no Servwell account at all). Every other
 * mutation is sent only while this tab writes as the account that made it. A guard test
 * requires this list to be exactly the server's mutations that run without a shop.
 */
export const NULL_ACCOUNT_WRITES: readonly string[] = [
  'team.acceptInvite',
  'public.approveQuote',
  'public.declineQuote',
];

export const isReplayable = (path: string | null): path is string =>
  path !== null && REPLAYABLE.includes(path);

/** A row read back from disk, which is untrusted storage: anything malformed is none. */
export function parsePendingWrite(raw: unknown): PendingWrite | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  const key = keyOf(r.variables);
  const madeBy = parseAccountRef(r.madeBy);
  if (!key || !madeBy || typeof r.path !== 'string' || !isReplayable(r.path)) return null;
  if (r.id !== idOf(key)) return null;
  return {
    id: r.id,
    clientId: key.clientId,
    mutationId: key.mutationId,
    path: r.path,
    variables: r.variables as Record<string, unknown>,
    madeBy,
    createdAt: typeof r.createdAt === 'number' ? r.createdAt : 0,
    source: r.source === 'migrated' ? 'migrated' : 'live',
    ...(typeof r.draftKey === 'string' ? { draftKey: r.draftKey } : {}),
  };
}

/** Any mutation in the cache, as TanStack's own cache events type them. */
type AnyMutation = Mutation<any, any, any, any>;

/** What this tab knows about one mutation in its cache. */
type Stamp = {
  /** Who made it (`null`: nobody signed in): at `onMutate`, or from its row when restored. */
  madeBy: AccountRef | null;
  /** The row it holds, for a replayable write. */
  rowId?: string;
};

const byCreatedAt = (a: PendingWrite, b: PendingWrite) => a.createdAt - b.createdAt;

/**
 * How long the store's first answer may take before the gate says it could not read it.
 * The gate never draws the shop without the rows: it holds the skeleton, then says so.
 */
const READ_PATIENCE_MS = 15_000;

/** Each write client's writes, for the hooks that read them through the QueryClient. */
const writesOfClient = new WeakMap<QueryClient, PendingWrites>();

/** The writes built with this QueryClient (`createWriteClient`), if it was built so. */
export function writesFor(queryClient: QueryClient): PendingWrites | undefined {
  return writesOfClient.get(queryClient);
}

export class PendingWrites {
  readonly #queryClient: QueryClient;
  readonly #store: PendingWriteStore;
  readonly #now: () => number;
  #handOff: WriteHandOff | null;

  /** Every row on the disk, every account's, as last read or written by this tab. */
  readonly #mirror = new Map<string, PendingWrite>();
  /**
   * Rows this tab delivered or handed off: a `load()` already in flight must not put them
   * back from the stale read it started before they left.
   */
  readonly #gone = new Set<string>();
  /**
   * One stamp per Mutation OBJECT. Not `meta`: `MutationObserver.setOptions` replaces a
   * pending mutation's options on every render, while it keeps the object.
   */
  readonly #stamps = new WeakMap<AnyMutation, Stamp>();
  /**
   * Originals whose kept error is on its way: removed from the cache the moment their own
   * `error` lands, so each row has one live mutation after the hand-over.
   */
  readonly #dropOnError = new WeakSet<AnyMutation>();
  #account: AccountRef | null = null;
  /**
   * When the network last confirmed `#account` (me.session's `dataUpdatedAt`), or null when
   * the tab draws the account saved on the device without a network answer (an offline
   * start). A replayable write waits for a confirmation.
   */
  #confirmedAt: number | null = null;
  /** A 401 came back: nothing replayable sends until the gate confirms again. */
  #suspended = false;
  /** Another tab changed account, or this one is signing out: nothing sends from here. */
  #stopped = false;
  #loaded = false;
  #loading: Promise<void> | null = null;
  #started = false;
  /**
   * The store did not answer: its read failed, or took longer than the patience. The gate
   * shows that it could not read the changes saved here (`unreadable`), with Try again
   * (`retryRead`), and draws nothing of the shop until a read lands.
   */
  #unreadable = false;
  #patience: ReturnType<typeof setTimeout> | undefined;
  readonly #readPatienceMs: number;
  /** Told when what the gate may draw changes (`readyFor`, `unreadable`). */
  readonly #listeners = new Set<() => void>();

  constructor(opts: {
    queryClient: QueryClient;
    store: PendingWriteStore;
    handOff?: WriteHandOff;
    now?: () => number;
    /** How long the store's first answer may take before the gate says so (15 s). */
    readPatienceMs?: number;
  }) {
    this.#readPatienceMs = opts.readPatienceMs ?? READ_PATIENCE_MS;
    this.#queryClient = opts.queryClient;
    this.#store = opts.store;
    this.#handOff = opts.handOff ?? null;
    this.#now = opts.now ?? (() => Date.now());
    // The exact Mutation object, and its own `error` action. TanStack calls the cache's
    // `onError` while the failing mutation is still `pending` and dispatches `error` only
    // afterwards, so the replacement is built first and the original leaves here.
    this.#cache().subscribe((event) => {
      if (event.type !== 'updated' || event.action.type !== 'error') return;
      if (!this.#dropOnError.has(event.mutation)) return;
      this.#dropOnError.delete(event.mutation);
      this.#cache().remove(event.mutation);
    });
  }

  #cache() {
    return this.#queryClient.getMutationCache();
  }

  /** Where final refusals go: the failed-write log (wired in identity.ts). */
  handOffTo(handOff: WriteHandOff): void {
    this.#handOff = handOff;
  }

  /**
   * The gate says which account this tab writes as: the account it draws, set in RootGate's
   * layout effect, so before paint and before any tap. `confirmedAt` is the network answer's
   * time, or null for the saved account drawn offline.
   *
   * - A different account (or nobody): every PAUSED mutation made by another account leaves
   *   memory. Their rows stay; a write in flight is parked by the guard at its next attempt.
   * - A new confirmation clears a 401's suspension.
   * - Then `sync()`: this account's rows not in memory are built, and resumed if they may go.
   */
  setAccount(next: AccountRef | null, confirmedAt: number | null = null): void {
    const account = accountRef(next);
    if (!sameAccountOrNobody(account, this.#account)) {
      for (const m of this.#cache().getAll()) {
        if (m.state.isPaused && !this.#belongsTo(m, account)) this.#cache().remove(m);
      }
    }
    if (account && confirmedAt !== null && confirmedAt !== this.#confirmedAt) {
      this.#suspended = false;
    }
    this.#account = account;
    this.#confirmedAt = account ? confirmedAt : null;
    this.sync();
    this.#notify();
  }

  /**
   * May the gate draw `account`'s screens yet? Only once this tab writes as that account and
   * the store has been READ, so that account's rows are in the cache. Then every guard on a
   * queued write reads it from its screen's first render, and so does every surface that
   * makes a replayable write without a guard (parts, van stock, timers, checklists): the shop
   * never draws over a queue it has not read, so no second write can be made over an unseen
   * one. A stopped tab draws its own screen. The read is local, so this costs nothing
   * unless the device's storage is in trouble (`unreadable`).
   */
  readyFor(account: AccountRef | null): boolean {
    if (this.#stopped) return true;
    if (!account || !sameAccount(account, this.#account)) return false;
    return this.#loaded;
  }

  /** The store's read failed, or has not answered within the patience: the gate says so. */
  unreadable(): boolean {
    return this.#unreadable && !this.#loaded;
  }

  /**
   * Try again, from the gate's "Couldn't read the changes saved on this device": a fresh
   * read (one that hangs is left behind, and lands harmlessly if it ever does), with the
   * patience started again.
   */
  retryRead(): void {
    if (this.#loaded) return;
    this.#unreadable = false;
    this.#loading = null;
    this.#notify();
    this.#armPatience();
    void this.load().catch((err: unknown) => {
      console.warn('[servwell] queued writes could not be read', err);
    });
  }

  /**
   * Has the store been read? Defence in depth behind the gate, which does not draw the shop
   * until it has: should a guarded control ever be drawn before, it treats every replayable
   * write as possibly pending (fail closed).
   */
  isRead(): boolean {
    return this.#loaded;
  }

  /** For `useSyncExternalStore`: told whenever `readyFor`, `unreadable` or `isRead` may change. */
  subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  };

  #notify(): void {
    for (const listener of [...this.#listeners]) listener();
  }

  /**
   * Another tab changed who is signed in, or this tab is signing out. From now on the guard
   * refuses every send from this tab, and paused writes leave memory while their rows stay.
   * Synchronous, so it lands before the other tab's cookie can.
   */
  stop(): void {
    this.#stopped = true;
    for (const m of this.#cache().getAll()) {
      if (m.state.isPaused) this.#cache().remove(m);
    }
    this.#notify();
  }

  /** Read the durable store into memory, then build this account's rows. Idempotent. */
  load(): Promise<void> {
    this.#loading ??= this.#store.all().then(
      (rows) => {
        for (const raw of rows) {
          const row = parsePendingWrite(raw);
          if (!row || this.#gone.has(row.id) || this.#mirror.has(row.id)) continue;
          this.#mirror.set(row.id, row);
        }
        this.#loaded = true;
        this.sync();
        this.#notify();
      },
      (err: unknown) => {
        // Memoising the rejection would leave every queued write unsent for the session.
        this.#loading = null;
        this.#couldNotRead();
        throw err;
      },
    );
    return this.#loading;
  }

  /**
   * Bring the queued writes back, once, after the persister's restore (which is where old
   * writes are moved into the store). A read that fails is tried again when the signal
   * returns or the tab is shown; until then the rows wait on the disk. Idempotent.
   */
  start(): void {
    if (this.#started) return;
    this.#started = true;
    this.#armPatience();
    const attempt = () => {
      if (this.#loaded) return;
      void this.load().catch((err: unknown) => {
        console.warn('[servwell] queued writes could not be read yet; trying again', err);
      });
    };
    attempt();
    onSignalOrVisible(attempt);
  }

  /**
   * Build the account's rows that are not already in memory, oldest first, as paused
   * mutations (what `hydrate()` does, de-duplicated by key), and resume them if they may go
   * now. Rows of anyone else are never built, and TanStack's resume walks only the cache, so
   * another account's write cannot be resumed from this tab.
   */
  sync(): void {
    const account = this.#account;
    if (!this.#loaded || this.#stopped || !account) return;
    const held = this.#idsInMemory();
    const mine = [...this.#mirror.values()]
      .filter((row) => sameAccount(row.madeBy, account) && !held.has(row.id))
      .sort(byCreatedAt);
    for (const row of mine) this.#build(row);
    if (this.#confirmedAt !== null && !this.#suspended) {
      void this.#queryClient.resumePausedMutations();
    }
  }

  /**
   * This account's writes waiting on this device, read fresh from the disk (so another tab's
   * count too). Best effort: a missed warning loses nothing.
   */
  async countFor(account: AccountRef): Promise<{ waiting: number; refused: number }> {
    const rows = await this.#store.all();
    const waiting = rows.filter((raw) =>
      sameAccount(parsePendingWrite(raw)?.madeBy, account),
    ).length;
    return { waiting, refused: 0 };
  }

  /**
   * Is a write of `path` whose `field` is `value` still waiting on this device for the server's
   * answer: its row in memory, any account's, from the tap (`onMutate` puts it there before its
   * first await, and TanStack calls `onMutate` synchronously inside `mutate()`) until it lands
   * or its refusal is in the failed-write log. Asked by the photo queue: a photo taken with
   * "Flag a problem" is held while its `ticket.flag` has not landed (ticket 03).
   *
   * ⚠️ Until the store has been read the answer is YES: a row not read yet may be that write,
   * and a photo sent ahead of its flag is the order this exists to keep, across a restart too.
   */
  awaits(path: string, field: string, value: string): boolean {
    if (!this.#loaded) return true;
    for (const row of this.#mirror.values()) {
      if (row.path === path && row.variables[field] === value) return true;
    }
    return false;
  }

  /**
   * May this tab send this mutation now? `null` if so; otherwise the error the write guard
   * fails it with. One rule for every mutation:
   *
   * - a stopped tab sends nothing;
   * - a replayable write goes only with its durable row, only while this tab writes as the
   *   account that made it (person AND shop), not after a 401 until the gate confirms again,
   *   and not before the session is confirmed after an offline start;
   * - any other mutation goes only while this tab writes as the account that made it, or,
   *   made with no account, when it is on the closed null-account list.
   */
  holdFor(path: string, input: unknown): Error | null {
    if (isReplayable(path)) {
      const key = keyOf(input);
      const row = key ? this.#mirror.get(idOf(key)) : undefined;
      if (!row) return new ParkedWriteError('not-on-device');
      const account = this.#account;
      if (
        this.#stopped ||
        !account ||
        !sameAccount(row.madeBy, account) ||
        this.#suspended
      ) {
        return new ParkedWriteError('kept');
      }
      if (this.#confirmedAt === null) {
        return onlineManager.isOnline()
          ? new ParkedWriteError('kept')
          : new NoSignalError();
      }
      return null;
    }
    if (this.#stopped) return new ParkedWriteError('account-changed');
    const candidates = this.#candidates(path, input);
    if (candidates.length === 0) return new ParkedWriteError('account-changed');
    if (candidates.some((m) => !this.#belongsTo(m, this.#account))) {
      return new ParkedWriteError('account-changed');
    }
    return null;
  }

  /**
   * The live mutations this operation can be: same path, pending, and holding this very
   * input object (tRPC hands the link the variables TanStack passed, unchanged). A mutation
   * no longer in the cache is not here, and is refused.
   */
  #candidates(path: string, input: unknown): AnyMutation[] {
    return this.#cache()
      .getAll()
      .filter(
        (m) =>
          m.state.status === 'pending' &&
          m.state.variables === input &&
          pathOf(m.options.mutationKey) === path,
      );
  }

  /** THE maker rule: may `account` send this mutation? */
  #belongsTo(m: AnyMutation, account: AccountRef | null): boolean {
    const stamp = this.#stamps.get(m);
    if (!stamp) return false;
    if (stamp.madeBy === null) {
      return NULL_ACCOUNT_WRITES.includes(pathOf(m.options.mutationKey) ?? '');
    }
    return sameAccount(stamp.madeBy, account);
  }

  /** Row ids held by a pending mutation in the cache, by the key in its variables. */
  #idsInMemory(except?: AnyMutation): Set<string> {
    const ids = new Set<string>();
    for (const m of this.#cache().getAll()) {
      if (m === except || m.state.status !== 'pending') continue;
      const key = keyOf(m.state.variables);
      if (key) ids.add(idOf(key));
    }
    return ids;
  }

  /** A row as a paused mutation, as `hydrate()` builds one; made by the row's account. */
  #build(row: PendingWrite): AnyMutation {
    const mutation = this.#cache().build(
      this.#queryClient,
      {
        mutationKey: [row.path.split('.')],
        // The draft it came from, so its landing clears that draft (offline-replay's onLanded).
        ...(row.draftKey ? { meta: { draftKey: row.draftKey } } : {}),
      },
      {
        status: 'pending',
        isPaused: true,
        variables: row.variables,
        submittedAt: row.createdAt,
        failureCount: 0,
        failureReason: null,
        error: null,
        data: undefined,
        context: undefined,
      },
    ) as AnyMutation;
    this.#stamps.set(mutation, { madeBy: row.madeBy, rowId: row.id });
    return mutation;
  }

  // ── The cache's hooks ──────────────────────────────────────────────────────

  /**
   * Awaited by TanStack after `dispatch(pending)` and before `retryer.start()`, for a NEW
   * mutation only: a restored one skips it, and is trusted because it came from the store.
   *
   * Every mutation is stamped with its maker. Before any network attempt:
   *
   * - any other write made while this tab draws an account the network has not confirmed
   *   (an offline start) is refused, visibly: it cannot queue, and may not go before
   *   Servwell knows who is signed in (the null-account flows aside);
   * - a replayable one is refused if nobody is signed in, if it has no idempotency key, or
   *   if the same key is already waiting; otherwise its row is in memory and, awaited, on
   *   the disk.
   */
  onMutate = async (variables: unknown, mutation: AnyMutation): Promise<void> => {
    const account = this.#account;
    this.#stamps.set(mutation, { madeBy: account });
    const path = pathOf(mutation.options.mutationKey);
    if (!isReplayable(path)) {
      const nullAccountFlow = NULL_ACCOUNT_WRITES.includes(path ?? '');
      if (account && this.#confirmedAt === null && !nullAccountFlow) {
        throw new NeedsConnectionError();
      }
      return;
    }
    if (!account) throw new UnownedWriteError();
    const key = keyOf(variables);
    if (!key) {
      console.error(
        `[servwell] refused ${path}: a replayable write needs an idempotency key`,
      );
      throw new UnkeyedWriteError(path);
    }
    const id = idOf(key);
    if (this.#mirror.has(id)) throw new DuplicateWriteError();
    const draftKey = draftKeyOf(mutation.options.meta);
    const row: PendingWrite = {
      id,
      ...key,
      path,
      variables: variables as Record<string, unknown>,
      madeBy: account,
      createdAt: this.#now(),
      source: 'live',
      ...(draftKey ? { draftKey } : {}),
    };
    // In memory first, so a second tap with the same key is refused while the put is in the
    // air; on the disk before the first attempt, or not sent at all.
    this.#mirror.set(id, row);
    try {
      await this.#store.put(row);
    } catch (err) {
      this.#mirror.delete(id);
      throw new StorageFullError(err);
    }
    this.#stamps.set(mutation, { madeBy: account, rowId: id });
  };

  /**
   * Delivered. ⚠️ Must never throw: TanStack awaits this INSIDE the `try` around the attempt,
   * so a throw would send a write the server took down the error path.
   */
  onSuccess = async (variables: unknown, mutation: AnyMutation): Promise<void> => {
    const id = this.#takeRow(mutation);
    if (id === undefined) return;
    this.#forget(id);
    try {
      await this.#store.remove(id);
    } catch (err) {
      // It replays at the next start, and the server's key absorbs it.
      console.warn(
        '[servwell] a delivered write could not be cleared from this device',
        err,
      );
    }
    const key = keyOf(variables);
    try {
      if (key) await this.#handOff?.landed(key.mutationId);
    } catch {
      // The log row comes back at the next reload for a write that already went through.
      // Annoying, not lossy.
    }
  };

  /**
   * The final failure (TanStack calls this once, never per attempt).
   *
   * - **Kept** (parked by the guard, or a 401): the row stays and nothing goes to the log.
   *   The failing mutation is REPLACED by a paused copy of its row when this tab still writes
   *   as the account that made it, so a control guarded on the queue never unlocks across the
   *   hand-over; the original leaves the cache once its own `error` lands. A 401 also
   *   suspends this tab's replayable sends and asks the gate who is signed in.
   * - **Refused**: written to the failed-write log under the ROW's account, awaited, and
   *   only then is the row removed.
   *
   * ⚠️ Must never throw: TanStack turns a throw here into an unhandled rejection.
   */
  onError = async (error: unknown, mutation: AnyMutation): Promise<void> => {
    try {
      const id = this.#stamps.get(mutation)?.rowId;
      if (id === undefined) return; // refused before it had a row, or not replayable
      const row = this.#mirror.get(id);
      if (!row) return;
      if (isKeptForAccount(error)) {
        if (refusalStatus(error) === 401) this.#signedOut();
        this.#replace(mutation, row);
        return;
      }
      const handOff = this.#handOff;
      if (!handOff) {
        console.error('[servwell] a refused write has nowhere to go; it stays queued');
        return;
      }
      await handOff.record({
        path: row.path,
        variables: row.variables,
        error,
        account: row.madeBy,
      });
      this.#takeRow(mutation);
      this.#forget(id);
      await this.#store.remove(id);
    } catch (err) {
      // The log refused it (or the delete did): the pending row stays, replays at the next
      // start, is refused again, and the log row is overwritten under the same key.
      console.warn('[servwell] a refused write stays queued on this device', err);
    }
  };

  /** A kept write's original leaves; its row is built again, paused, if it is ours to hold. */
  #replace(original: AnyMutation, row: PendingWrite): void {
    this.#takeRow(original);
    this.#dropOnError.add(original);
    if (this.#stopped || !sameAccount(row.madeBy, this.#account)) return;
    // The original is still `pending` here: it must not count as already holding the row.
    if (this.#idsInMemory(original).has(row.id)) return;
    this.#build(row);
  }

  /** The row this mutation holds, released from it. */
  #takeRow(mutation: AnyMutation): string | undefined {
    const stamp = this.#stamps.get(mutation);
    const id = stamp?.rowId;
    if (stamp && id !== undefined) this.#stamps.set(mutation, { madeBy: stamp.madeBy });
    return id;
  }

  /** A 401: hold every replayable send, and have the gate ask who is signed in. */
  #signedOut(): void {
    if (this.#suspended) return;
    this.#suspended = true;
    void this.#queryClient.resetQueries({
      predicate: (q) => isSessionQueryKey(q.queryKey),
    });
  }

  /** Past the patience with no answer, the gate stops showing a skeleton and says why. */
  #armPatience(): void {
    clearTimeout(this.#patience);
    this.#patience = setTimeout(() => {
      if (!this.#loaded) this.#couldNotRead();
    }, this.#readPatienceMs);
  }

  #couldNotRead(): void {
    if (this.#unreadable) return;
    this.#unreadable = true;
    this.#notify();
  }

  #forget(id: string): void {
    this.#mirror.delete(id);
    this.#gone.add(id);
  }

  /** Test seam: what this tab knows, read-only. */
  readonly inspect = {
    madeBy: (m: AnyMutation): AccountRef | null | undefined =>
      this.#stamps.get(m)?.madeBy,
    row: (id: string): PendingWrite | undefined => this.#mirror.get(id),
    stopped: (): boolean => this.#stopped,
    handOff: (): WriteHandOff | null => this.#handOff,
  };
}

/** The mutation cache's config, calling into the writes built after it. */
function writeCacheConfig(writes: () => PendingWrites) {
  return {
    onMutate: (variables: unknown, mutation: AnyMutation) =>
      writes().onMutate(variables, mutation),
    onSuccess: (
      _data: unknown,
      variables: unknown,
      _context: unknown,
      mutation: AnyMutation,
    ) => writes().onSuccess(variables, mutation),
    onError: (
      error: unknown,
      _variables: unknown,
      _context: unknown,
      mutation: AnyMutation,
    ) => writes().onError(error, mutation),
  };
}

/**
 * The QueryClient and its writes, built together: the app's (query.ts) and every test's,
 * so a test runs the very composition the app does.
 */
export function createWriteClient(opts: {
  store: PendingWriteStore;
  handOff?: WriteHandOff;
  defaultOptions?: DefaultOptions;
  now?: () => number;
  readPatienceMs?: number;
}): { queryClient: QueryClient; writes: PendingWrites } {
  let writes: PendingWrites | undefined;
  const mutationCache = new MutationCache(writeCacheConfig(() => writes!));
  const queryClient = new QueryClient({
    mutationCache,
    defaultOptions: {
      ...opts.defaultOptions,
      mutations: {
        networkMode: 'offlineFirst',
        retry: shouldRetryWrite,
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30_000), // cap 30s
        ...opts.defaultOptions?.mutations,
      },
    },
  });
  writes = new PendingWrites({
    queryClient,
    store: opts.store,
    handOff: opts.handOff,
    now: opts.now,
    readPatienceMs: opts.readPatienceMs,
  });
  writesOfClient.set(queryClient, writes);
  return { queryClient, writes };
}

// ── The real store ───────────────────────────────────────────────────────────

/** Its own database, like `servwell-uploads` and `servwell-failed-writes`. */
export const PENDING_WRITES_DB = 'servwell-pending-writes';

let idb: UseStore | undefined;
/** Opened lazily: this module is imported by tests that run in Node, where there is none. */
const store = (): UseStore => (idb ??= createStore(PENDING_WRITES_DB, 'writes'));

/**
 * A queued payment must not be evicted by cache housekeeping, nor rewritten whenever a query
 * changes, so it lives in a database of its own.
 */
export const indexedDbPendingWriteStore: PendingWriteStore & {
  /** Copy only if absent, in ONE readwrite transaction (the move of old writes). */
  putIfAbsent(row: PendingWrite): Promise<void>;
} = {
  all: () => values(store()),
  put: (row) => set(row.id, row, store()),
  remove: (id) => del(id, store()),
  putIfAbsent: (row) => update(row.id, (current: unknown) => current ?? row, store()),
};
